<template>
  <v-dialog
    v-model="dialog"
    max-width="520"
    overlay-opacity="0.8"
    :persistent="loading"
  >
    <v-card class="rounded-xl">
      <v-card-title class="text-body-1 font-weight-bold">
        Alterar dados
      </v-card-title>
      <v-card-text>
        <p class="text-caption mb-4">
          Telefone e senha não podem ser alterados.
        </p>

        <v-text-field
          :value="phone"
          label="Telefone"
          outlined
          dense
          disabled
          persistent-hint
          hint="O telefone não pode ser alterado"
        />

        <v-text-field
          v-model="form.name"
          label="Nome"
          outlined
          dense
          autocomplete="off"
          :disabled="loading"
          :error-messages="fieldErrors.name"
          @input="clearField('name')"
        />

        <v-text-field
          v-model="form.email"
          label="E-mail"
          outlined
          dense
          type="email"
          autocomplete="off"
          autocapitalize="none"
          spellcheck="false"
          :disabled="loading"
          :error-messages="emailErrors"
          @input="clearField('email')"
        />

        <v-text-field-simplemask
          v-model="form.document"
          label="CPF"
          :properties="cpfProperties"
          :options="cpfOptions"
          @input="clearField('document')"
        />

        <v-text-field
          v-model="form.birthday"
          label="Data de nascimento"
          type="date"
          outlined
          dense
          :max="maxBirthday"
          :disabled="loading"
          :error-messages="fieldErrors.birthday"
          @input="clearField('birthday')"
        />

        <v-alert v-if="error" type="error" dense text class="mb-0">
          {{ error }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-btn text :disabled="loading" @click="close">Cancelar</v-btn>
        <v-spacer />
        <v-btn color="primary" :loading="loading" :disabled="!canSave" @click="save">
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import USERS from "@/services/ultimate/users";
import validateCpf from "@/utils/validate-cpf";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const digitsOf = (value) => String(value || "").replace(/\D/g, "");

const formatCpf = (value) => {
  const digits = digitsOf(value);
  if (digits.length !== 11) return value || "";
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

const toDateInput = (value) => {
  if (!value) return "";
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

export default {
  name: "UserEditModal",
  data: () => ({
    dialog: false,
    loading: false,
    userId: null,
    phone: "",
    error: null,
    fieldErrors: {},
    form: {
      name: "",
      email: "",
      document: "",
      birthday: "",
    },
    cpfOptions: {
      inputMask: "###.###.###-##",
      outputMask: "###.###.###-##",
      applyAfter: false,
      alphanumeric: false,
      lowerCase: false,
    },
  }),
  computed: {
    maxBirthday() {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      return `${now.getFullYear()}-${month}-${day}`;
    },
    emailErrors() {
      if (this.fieldErrors.email) return [this.fieldErrors.email];
      const email = this.form.email.trim();
      if (email && !EMAIL_PATTERN.test(email)) return ["Informe um e-mail válido"];
      return [];
    },
    documentErrors() {
      if (this.fieldErrors.document) return [this.fieldErrors.document];
      const digits = digitsOf(this.form.document);
      if (!digits || validateCpf(digits)) return [];
      if (digits.length < 11) return ["Informe o CPF completo"];
      return ["CPF inválido"];
    },
    cpfProperties() {
      return {
        outlined: true,
        dense: true,
        type: "tel",
        autocomplete: "off",
        placeholder: "###.###.###-##",
        disabled: this.loading,
        errorMessages: this.documentErrors,
      };
    },
    canSave() {
      if (this.loading || !this.userId) return false;
      const email = this.form.email.trim();
      if (email && !EMAIL_PATTERN.test(email)) return false;
      const digits = digitsOf(this.form.document);
      if (digits && !validateCpf(digits)) return false;
      return true;
    },
  },
  methods: {
    open(user) {
      this.userId = user?.id || null;
      this.phone = user?.phone ? `+${user.ddi || 55} ${user.phone}` : "Não informado";
      this.form = {
        name: user?.name || "",
        email: user?.email || "",
        document: formatCpf(user?.document),
        birthday: toDateInput(user?.birthday),
      };
      this.error = null;
      this.fieldErrors = {};
      this.loading = false;
      this.dialog = true;
    },
    close() {
      if (this.loading) return;
      this.dialog = false;
    },
    clearField(field) {
      if (!this.fieldErrors[field]) return;
      this.$delete(this.fieldErrors, field);
    },
    async save() {
      if (!this.canSave) return;
      this.loading = true;
      this.error = null;
      this.fieldErrors = {};

      try {
        await USERS.updateUser(this.userId, {
          name: this.form.name.trim(),
          email: this.form.email.trim().toLowerCase(),
          document: digitsOf(this.form.document) ? this.form.document : "",
          birthday: this.form.birthday || "",
        });
        this.loading = false;
        this.dialog = false;
        this.$emit("saved");
      } catch (error) {
        const fields = {};
        (error.fields || []).forEach((item) => {
          if (item.field) fields[item.field] = item.message;
        });
        if (!Object.keys(fields).length && error.field) {
          fields[error.field] = error.message;
        }
        this.fieldErrors = fields;
        this.error = Object.keys(fields).length
          ? null
          : error.message || "Erro ao alterar os dados do cliente";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
